import * as React from "react";
import { useEffect, useMemo } from "react";
import {
  Control,
  DeepPartial,
  FieldValues,
  Path,
  useForm,
} from "react-hook-form";
import { CustomFilterOption } from "../custom-filters-list.component";

export type FilterOption<T extends FieldValues, K> = {
  name: string;
  label: string;
  editComponent: (
    name: string,
    setMetadata: (metadata: any) => void,
    control: Control<T>,
    updateFilter: (value: K[keyof K] | null) => void
  ) => React.ReactNode;
  renderViewMode: (filterKey: string, metadata: any) => React.ReactNode;
  onDelete?: () => void;
};

type UseCustomFiltersProps<T extends FieldValues, K> = {
  updateCustomFilter: (
    filterName: keyof K,
    filterValue: K[keyof K] | undefined | null
  ) => void;
  filterOptions: FilterOption<T, K>[];
  selectedCustomFilters: T;
  setSelectedCustomFilters: React.Dispatch<React.SetStateAction<T>>;
};

export default function useCustomFilters<T extends FieldValues, K>({
  updateCustomFilter,
  filterOptions,
  selectedCustomFilters,
  setSelectedCustomFilters,
}: UseCustomFiltersProps<T, K>) {
  const defaultValues = useMemo(() => {
    const initialValues = filterOptions.reduce((acc, filter) => {
      acc[filter.name as keyof T] =
        selectedCustomFilters[filter.name as keyof T] || undefined;
      return acc;
    }, {} as Partial<T>);

    return initialValues as DeepPartial<T>;
  }, [selectedCustomFilters, filterOptions]);

  const formMethods = useForm<T>({
    mode: "onChange",
    defaultValues: defaultValues as any,
  });

  const { control, reset, setValue } = formMethods;

  useEffect(() => {
    reset(defaultValues as T);
  }, [defaultValues, reset]);

  const filters: CustomFilterOption[] = useMemo(() => {
    return filterOptions.map((option) => {
      const customFilterOption: CustomFilterOption = {
        label: option.label,
        filterKey: option.name,
        renderViewMode: option.renderViewMode,
        editComponent: (filterKey, setMetadata) =>
          option.editComponent(
            filterKey,
            (metadata) => {
              setValue(filterKey as Path<T>, metadata as any);
              setSelectedCustomFilters((prev) => ({
                ...prev,
                [filterKey]: metadata,
              }));
              setMetadata(metadata);
            },
            control,
            (value) => updateCustomFilter(filterKey as keyof K, value)
          ),
        onDelete: () => {
          setValue(option.name as Path<T>, null as any);
          setSelectedCustomFilters((prev) => ({
            ...prev,
            [option.name as Path<T>]: null,
          }));
          updateCustomFilter(option.name as keyof K, undefined);
          option.onDelete?.();
        },
      };
      return customFilterOption;
    });
  }, [
    control,
    setValue,
    updateCustomFilter,
    filterOptions,
    setSelectedCustomFilters,
  ]);

  return filters;
}
